import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { DialogContent, DialogActions, Typography, TextField } from '@material-ui/core'
import * as Modal from '_components/modal/generic-modal'
import Button from '_components/button'
import { Attachments } from '_/components/refera-ui'
import { useToast } from '_/hooks/use-toast'
import { completeCanceledServiceOrder } from '_modules/service-orders/actions'
import { getHistoryLogs } from '_modules/history-logs/actions'

import useStyles from './styles'

const CompleteServiceOrderModal = ({
  isOpen,
  handleClose,
  serviceOrderId,
  handleGetServiceOrderActive,
}) => {
  const styles = useStyles()
  const { showToast } = useToast()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [details, setDetails] = useState('')
  const [files, setFiles] = useState([])

  const handleResetAndClose = useCallback(() => {
    setDetails('')
    setFiles([])
    handleClose()
  }, [handleClose])

  const handleCompleteServiceOrder = useCallback(() => {
    setIsLoading(true)
    const payload = {
      details,
      files,
    }

    dispatch(completeCanceledServiceOrder(serviceOrderId, payload))
      .then(() => {
        dispatch(getHistoryLogs(serviceOrderId))
        handleGetServiceOrderActive()
        handleResetAndClose()
        showToast({
          type: 'success',
          message: 'Chamado concluído com sucesso.',
        })
      })
      .catch(err => {
        const message = err?.message || 'Ocorreu um erro ao concluir o chamado.'
        showToast({
          type: 'error',
          message,
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [
    dispatch,
    handleResetAndClose,
    handleGetServiceOrderActive,
    serviceOrderId,
    showToast,
    details,
    files,
  ])

  const handleDrop = useCallback(acceptedFiles => {
    setFiles(prev => [...prev, ...acceptedFiles])
  }, [])

  const handleRemove = useCallback(index => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }, [])

  return (
    <Modal.Root
      open={isOpen}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return
        handleResetAndClose()
      }}
    >
      <div className={styles.header}>
        <Typography className={styles.title}>Concluir ID #{serviceOrderId}</Typography>
      </div>
      <DialogContent className={styles.content}>
        <div>
          <Typography className={styles.mainTitle}>Deseja concluir o chamado?</Typography>
          <Typography className={styles.link} onClick={() => {}}>
            Clique aqui para saber mais
          </Typography>
        </div>

        <div>
          <Typography className={styles.label}>Detalhes sobre a conclusão</Typography>
          <TextField
            className={styles.textArea}
            multiline
            rows={4}
            placeholder="Mais detalhes"
            variant="outlined"
            value={details}
            autoFocus
            onChange={e => setDetails(e.target.value)}
            inputProps={{
              onKeyDown: e => e.stopPropagation(),
            }}
          />
        </div>

        <div>
          <Typography className={styles.label}>Anexos</Typography>
          <Attachments
            label="Imagens e vídeos"
            files={files}
            onDrop={handleDrop}
            onRemoveItem={handleRemove}
            readOnly={false}
            accept={{
              'image/*': [],
              'video/*': [],
              '.pdf': [],
            }}
          />
        </div>
      </DialogContent>

      <DialogActions className={styles.actions}>
        <Button
          variant="outlined"
          className={`${styles.button} ${styles.buttonOutline}`}
          fullWidth
          onClick={handleResetAndClose}
          disabled={isLoading}
        >
          Desfazer e cancelar a conclusão
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={styles.button}
          fullWidth
          onClick={handleCompleteServiceOrder}
          isLoading={isLoading}
        >
          Confirmar e concluir
        </Button>
      </DialogActions>
    </Modal.Root>
  )
}

CompleteServiceOrderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  serviceOrderId: PropTypes.string.isRequired,
  handleGetServiceOrderActive: PropTypes.func.isRequired,
}

export default CompleteServiceOrderModal
